import type {
  ConnexionSonde, EtatSmtpSonde, Lecture, ProprietairesSonde, SecoursSonde, SondeSourceView,
} from './api.js'

/**
 * « RACCORDER SONDE » — le parcours en cinq cases, DÉDUIT DES DONNÉES RÉELLES.
 *
 * ORDRE : « Alertes publiées » AVANT « Sources confiées » — confier une source
 * à une Sonde en ombre complète ferait taire tout le monde pour elle (le
 * serveur refuse d'ailleurs la bascule dans ce cas).
 *
 * « Activer n'est pas faire » : une case n'est cochée que par ce qui s'est
 * réellement passé — Sonde a répondu avec les jetons, une source a été lue
 * avec sa clé, la recopie du SMTP a abouti, la propriété est enregistrée,
 * Sonde dit elle-même ce qu'elle publie. Jamais par un réglage posé.
 * Ce qu'on ne peut pas lire est « — », jamais « à faire » ni « fait ».
 */

export type EtatEtape = 'fait' | 'en_cours' | 'a_faire' | 'inconnu'

export interface Etape {
  id: 'jetons' | 'cles' | 'smtp' | 'sources' | 'alertes'
  titre: string
  etat: EtatEtape
  detail: string
}

export function etapeJetons(c: Lecture<ConnexionSonde> | null): Etape {
  const base = { id: 'jetons' as const, titre: 'Jetons collés' }
  if (!c || c.etat !== 'ok') return { ...base, etat: 'inconnu', detail: '—' }
  const d = c.data
  const n = Number(d.tokens.api.set) + Number(d.tokens.worker.set)
  if (!d.configured && n === 0) return { ...base, etat: 'a_faire', detail: 'Adresse et jetons de Sonde à saisir.' }
  if (n < 2) return { ...base, etat: n === 0 ? 'a_faire' : 'en_cours', detail: `${n} jeton sur 2 enregistré${n > 1 ? 's' : ''}.` }
  if (!d.status.reachable) return { ...base, etat: 'en_cours', detail: 'Jetons enregistrés, mais Sonde ne répond pas encore.' }
  return { ...base, etat: 'fait', detail: 'Sonde répond avec les jetons de Gestion.' }
}

/** Les sources qui ont une clé de compte lecteur à saisir (le SMTP est recopié tout seul). */
export function sourcesACle(sources: SondeSourceView[]): SondeSourceView[] {
  return sources.filter((s) => s.credential.fields.length > 0 && s.code !== 'smtp')
}

export function etapeCles(l: Lecture<{ sources: SondeSourceView[] }> | null): Etape {
  const base = { id: 'cles' as const, titre: 'Clés des comptes lecteur' }
  if (!l || l.etat !== 'ok') return { ...base, etat: 'inconnu', detail: '—' }
  const liste = sourcesACle(l.data.sources)
  if (liste.length === 0) return { ...base, etat: 'inconnu', detail: '—' }
  // Une source compte quand Sonde l'a LUE avec succès, pas quand une clé est posée.
  const lues = liste.filter((s) => s.status === 'OK').length
  const posees = liste.filter((s) => s.credential.configured).length
  const etat: EtatEtape = lues === liste.length ? 'fait' : posees > 0 ? 'en_cours' : 'a_faire'
  return { ...base, etat, detail: `${lues} source${lues > 1 ? 's' : ''} sur ${liste.length} lue${lues > 1 ? 's' : ''} avec succès.` }
}

/** Secours e-mail : le SMTP recopié ET au moins un destinataire (sans lui, la sentinelle n'envoie rien). */
export function etapeSmtp(l: Lecture<EtatSmtpSonde> | null, secours?: Lecture<SecoursSonde> | null): Etape {
  const base = { id: 'smtp' as const, titre: 'Secours e-mail' }
  if (!l || l.etat !== 'ok') return { ...base, etat: 'inconnu', detail: '—' }
  const sansDestinataire = secours?.etat === 'ok' && secours.data.recipients.length === 0
  if (l.data.last_attempt_at === null) return { ...base, etat: 'a_faire', detail: 'SMTP jamais recopié.' }
  if (l.data.ok !== true) return { ...base, etat: 'en_cours', detail: 'Dernière recopie du SMTP en attente ou refusée.' }
  if (sansDestinataire) return { ...base, etat: 'a_faire', detail: 'SMTP recopié, mais aucun destinataire de secours : la sentinelle ne peut rien envoyer.' }
  if (secours?.etat !== 'ok') return { ...base, etat: 'en_cours', detail: 'SMTP recopié ; destinataires de secours illisibles.' }
  return { ...base, etat: 'fait', detail: `SMTP recopié, ${secours.data.recipients.length} destinataire${secours.data.recipients.length > 1 ? 's' : ''} de secours.` }
}

export function etapeSources(l: Lecture<ProprietairesSonde> | null): Etape {
  const base = { id: 'sources' as const, titre: 'Sources confiées à Sonde' }
  if (!l || l.etat !== 'ok' || l.data.owners.length === 0) return { ...base, etat: 'inconnu', detail: '—' }
  const n = l.data.owners.filter((o) => o.owner === 'sonde').length
  const total = l.data.owners.length
  return {
    ...base,
    etat: n === total ? 'fait' : n > 0 ? 'en_cours' : 'a_faire',
    detail: `${n} sur ${total} surveillée${n > 1 ? 's' : ''} par Sonde.`,
  }
}

/** Ce que Sonde publie, lu dans SON état (GET /v1/status relayé), jamais dans un brouillon. */
export function etapeAlertes(c: Lecture<ConnexionSonde> | null): Etape {
  const base = { id: 'alertes' as const, titre: 'Alertes publiées' }
  if (!c || c.etat !== 'ok' || !c.data.status.reachable || c.data.status.shadow_mode === null) return { ...base, etat: 'inconnu', detail: '—' }
  const { shadow_mode: ombre, shadow_release: rel, shadow_summary_fr: resume } = c.data.status
  if (!ombre) return { ...base, etat: 'fait', detail: 'Sonde publie toutes ses alertes.' }
  if (rel === 'switched_and_new') return { ...base, etat: 'fait', detail: resume ?? 'Sources basculées et tout ce que Gestion ne surveillait pas.' }
  if (rel === 'switched') return { ...base, etat: 'en_cours', detail: resume ?? 'Seulement les sources basculées.' }
  return { ...base, etat: 'a_faire', detail: resume ?? 'Sonde observe sans alerter.' }
}
